<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import { AUTOMATION_RULE_EVENTS } from './constants';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle', 'edit', 'delete', 'clone']);

const readableDate = date => messageStamp(new Date(date), 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date), 'LLL d, yyyy hh:mm a');

const translationKey = value =>
  value
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_');

const knownEventNames = new Set(AUTOMATION_RULE_EVENTS.map(event => event.key));

const eventLabel = computed(() => {
  const eventName = props.automation.event_name?.trim().toLowerCase();
  return knownEventNames.has(eventName)
    ? `AUTOMATION.EVENTS.${translationKey(eventName)}`
    : 'AUTOMATION.EVENTS.UNKNOWN';
});

const conditionCount = computed(() =>
  Array.isArray(props.automation.conditions)
    ? props.automation.conditions.length
    : 0
);

const actionCount = computed(() =>
  Array.isArray(props.automation.actions) ? props.automation.actions.length : 0
);

const actionLabels = computed(() =>
  (Array.isArray(props.automation.actions) ? props.automation.actions : [])
    .map(action => action?.action_name)
    .filter(Boolean)
    .map(action => `AUTOMATION.ACTIONS.${translationKey(action)}`)
);

const automationActive = computed({
  get: () => props.automation.active,
  set: active => {
    const { id, name } = props.automation;
    emit('toggle', {
      id,
      name,
      status: !active,
    });
  },
});
</script>

<template>
  <BaseTableRow :item="automation">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-body-main text-n-slate-12 truncate">
            {{ automation.name }}
          </span>
          <div class="w-px h-3 rounded-xl bg-n-weak flex-shrink-0" />
          <span class="text-body-main text-n-slate-11 truncate">
            {{ automation.description }}
          </span>
        </div>
        <div
          class="flex flex-wrap items-center gap-2 mt-1 text-sm text-n-slate-11"
        >
          <span class="inline-flex items-center gap-1">
            <span
              class="i-ph-lightning size-4 text-n-slate-10"
              aria-hidden="true"
            />
            {{ $t(eventLabel) }}
          </span>
          <span class="h-4 border-l border-n-weak" aria-hidden="true" />
          <span>
            {{ $t('AUTOMATION.LIST.CONDITIONS', { n: conditionCount }) }}
          </span>
          <span class="h-4 border-l border-n-weak" aria-hidden="true" />
          <span>
            {{ $t('AUTOMATION.LIST.ACTIONS', { n: actionCount }) }}
          </span>
          <span v-if="actionLabels.length" class="sr-only">
            {{
              $t('AUTOMATION.LIST.ACTIONS_DETAIL', {
                actions: actionLabels.map(key => $t(key)).join(', '),
              })
            }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <ToggleSwitch
          v-model="automationActive"
          :aria-label="
            $t('AUTOMATION.LIST.TOGGLE_ARIA', { name: automation.name })
          "
        />
      </BaseTableCell>

      <BaseTableCell :title="readableDateWithTime(automation.created_on)">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ readableDate(automation.created_on) }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
            :aria-label="
              $t('AUTOMATION.LIST.EDIT_ARIA', { name: automation.name })
            "
            icon="i-woot-edit-pen"
            slate
            sm
            :is-loading="loading"
            @click="$emit('edit', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
            :aria-label="
              $t('AUTOMATION.LIST.CLONE_ARIA', { name: automation.name })
            "
            icon="i-woot-clone"
            sm
            slate
            :is-loading="loading"
            @click="$emit('clone', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
            :aria-label="
              $t('AUTOMATION.LIST.DELETE_ARIA', { name: automation.name })
            "
            :is-loading="loading"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete', automation)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
